import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IonContent, IonSpinner } from '@ionic/angular/standalone';
import { TranslatePipe, _ } from '@ngx-translate/core';
import { Observable, filter, tap, throwError } from 'rxjs';
import { FooterComponent } from 'src/app/components/footer/footer.component';
import { TodoList } from 'src/app/models/todo-list';
import { AuthService } from 'src/app/services/auth.service';
import { TodoListService } from 'src/app/services/todo-list.service';
import { TranslateService } from 'src/app/services/translate.service';
import { LoadingUtils } from 'src/app/utils/loading.utils';
import { CreateOfferData, CreateOfferTab, TabSlug } from '../../models/create-offer/create-offer-data';
import { Offer, OfferGroup, OfferInterface } from '../../models/offer';
import { User } from '../../models/user';
import { OfferService } from '../../services/offer.service';
import { ToastService } from '../../services/toast.service';
import { CategoryUtils } from '../../utils/category.utils';
import { scrollListingWizardOnStepChange } from '../../utils/listing-wizard-scroll.utils';
import { MissingVariantPriceError, buildCreateOfferPayload, buildUpdateOfferPayload } from '../../utils/offer-payload.utils';
import { PickAddressesComponent } from './components/pick-addresses/pick-addresses.component';
import { PickCategoryComponent } from './components/pick-category/pick-category.component';
import { PickFiltersComponent } from './components/pick-filters/pick-filters.component';
import { PickMediasComponent } from './components/pick-medias/pick-medias.component';
import { PickPlatformComponent } from './components/pick-platform/pick-platform.component';
import { PickPricesComponent } from './components/pick-prices/pick-prices.component';

@Component({
    selector: 'app-create-offer',
    templateUrl: './create-offer.page.html',
    styleUrls: ['./create-offer.page.scss'],
    imports: [
        CommonModule,
        CommonModule,
        IonContent,
        IonSpinner,
        PickCategoryComponent,
        PickFiltersComponent,
        PickMediasComponent,
        PickPlatformComponent,
        PickPricesComponent,
        // PickAddonsComponent,
        PickAddressesComponent,
        FooterComponent,
        TranslatePipe,
    ],
})
export class CreateOfferPage implements OnInit {
    @Input() isEdit = false;
    @Input() initialData?: CreateOfferData;
    @Input() offerToEdit?: Offer;
    @Input() editValidationStatus?: string;
    @Input() resubmitting = false;
    @Output() resubmit = new EventEmitter<void>();

    data: CreateOfferData;
    tab: TabSlug = 'platform';
    tabs: CreateOfferTab[] = [
        { slug: 'platform', text: _('Platform'), disabled: false },
        { slug: 'category', text: _('Category'), disabled: false },
        { slug: 'filters', text: _('Details'), disabled: false },
        { slug: 'price', text: _('Price'), disabled: false },
        { slug: 'location', text: _('Location'), disabled: false },
        { slug: 'photos', text: _('Photos'), disabled: false },
        { slug: 'boost', text: _('Boost'), color: 'danger', icon: 'lightning', last: true, disabled: false },
    ];
    user: User;
    protected submittingOffer: boolean;
    authorized: boolean = null;
    todoList: TodoList;

    private shouldResetWizard = false;
    private readonly elementRef = inject(ElementRef);
    private readonly router = inject(Router);

    constructor(
        private readonly offerService: OfferService,
        private readonly toastService: ToastService,
        private readonly authService: AuthService,
        private readonly todoListService: TodoListService,
        private readonly translateService: TranslateService,
    ) {}

    ngOnInit() {
        if (this.isEdit) {
            this.initForEdit();
        }
    }

    ionViewWillEnter() {
        if (this.isEdit) {
            return;
        }
        if (this.shouldResetWizard) {
            this.resetCreateWizard();
            this.shouldResetWizard = false;
        }
        this.authorized = null;
        this.authService.userLoggedIn.pipe(filter((user) => !!user)).subscribe({
            next: async (user: User) => {
                this.user = user;
                this.todoList = await this.todoListService.create(user);
                this.authorized = this.todoList.done;
                if (user.pro) {
                    this.authorized = this.authorized && user.status === 'validated';
                }
            },
        });
    }

    private initForEdit(): void {
        this.data = this.initialData ?? CreateOfferData.createEmpty();
        this.authorized = true;
        this.setLocationTab();
        this.tab = 'filters';
        this.authService.userLoggedIn.pipe(filter((user) => !!user)).subscribe({
            next: (user: User) => {
                this.user = user;
            },
        });
    }

    async publishOffer() {
        if (this.submittingOffer) {
            return;
        }
        this.submittingOffer = true;
        await LoadingUtils.show();

        const offers: OfferInterface[] = [];

        try {
            if (this.data.filterData.variantGroups.length === 0) {
                offers.push(buildCreateOfferPayload(this.data, this.user));
            } else {
                for (const group of this.data.filterData.variantGroups) {
                    offers.push(buildCreateOfferPayload(this.data, this.user, group));
                }
            }
        } catch (err) {
            this.toastService.error(
                err instanceof MissingVariantPriceError ? _('each_variant_must_have_a_price') : _('Error creating offer'),
            );
            this.submittingOffer = false;
            LoadingUtils.hide();
            return;
        }

        // If we have more than one offer, we need to create the offer group
        if (offers.length > 1) {
            const offerGroup = await this.createOfferGroup();
            // Add the offer group id to each offer
            for (const offer of offers) {
                offer.offerGroup = offerGroup.id;
            }
        }

        let firstOffer: Offer = null;
        let anyPendingValidation = false;
        for (const offer of offers) {
            const success = await new Promise<boolean>((resolve) => {
                this.offerService.create(offer).subscribe({
                    next: (o) => {
                        if (!firstOffer) {
                            firstOffer = o;
                        }
                        if (o.pendingValidation) {
                            anyPendingValidation = true;
                        }
                        resolve(true);
                    },
                    error: (err) => {
                        this.toastService.error(this.formatBackendErrorForParent(err, _('Error creating offer')));
                        this.submittingOffer = false;
                        LoadingUtils.hide();
                        resolve(false);
                    },
                });
            });
            if (!success) {
                return;
            }
        }

        if (anyPendingValidation) {
            this.shouldResetWizard = true;
            await this.router.navigateByUrl('/offer-pending-validation', { replaceUrl: true });
            this.resetCreateWizard();
            await this.toastService.success(_('offer_submitted_pending_toast'));
        } else {
            window.location.href = '/' + firstOffer.platform + '/offers/' + firstOffer.id + '-' + firstOffer.slug;
            await this.toastService.success(_('Offer created successfully'));
        }
        LoadingUtils.hide();

        this.submittingOffer = false;
    }

    private async createOfferGroup() {
        return new Promise<OfferGroup>((resolve) => {
            this.offerService.createGroup().subscribe({
                next: (group) => {
                    resolve(group);
                },
            });
        });
    }

    get editPlatformLabel(): string | null {
        return this.data?.platformData?.platform ?? null;
    }

    get isSavingOffer(): boolean {
        return !!this.submittingOffer;
    }

    get editCategoryFullName(): string {
        const category = this.data?.categoryData?.category;
        return category ? CategoryUtils.getFullName(category) : '';
    }

    selectTab(tab: TabSlug, force = false): void {
        if (!force && !this.canSelectTab(tab)) {
            return;
        }
        const previous = this.tab;
        this.tab = tab;
        scrollListingWizardOnStepChange(previous, tab, this.elementRef.nativeElement);
    }

    canSelectTab(tab: TabSlug): boolean {
        if (this.isEdit) {
            return true;
        }
        const newTabIndex = this.tabs.findIndex((t) => t.slug === tab);
        const currentTabIndex = this.tabs.findIndex((t) => t.slug === this.tab);

        // We can only select the previous tabs
        return newTabIndex <= currentTabIndex;
    }

    private resetCreateWizard(): void {
        if (this.isEdit) {
            return;
        }
        this.tab = 'platform';
        this.data = CreateOfferData.createEmpty();
        this.submittingOffer = false;
        const locationTab = this.tabs.find((t) => t.slug === 'location');
        if (locationTab) {
            locationTab.disabled = false;
        }
    }

    private setLocationTab(): void {
        const shippable = CategoryUtils.findProperty(this.data.categoryData.category, 'shippable');
        const platform = this.data.platformData.platform;
        const verticalAd = platform === 'motors' || platform === 'estate';
        this.tabs.find((t) => t.slug === 'location').disabled = !verticalAd && !!shippable;
    }

    shouldUseLocationModal(): boolean {
        if (!this.data?.platformData?.platform || !this.data?.categoryData?.category) {
            return false;
        }
        const platform = this.data.platformData.platform;
        const shippable = CategoryUtils.findProperty(this.data.categoryData.category, 'shippable');
        if (platform === 'motors' || platform === 'estate') {
            return true;
        }
        return platform === 'gazette' && !shippable;
    }

    nextStep($event: CreateOfferData) {
        this.data = $event;
        // Select next enabled tab
        const currentTabIndex = this.tabs.findIndex((t) => t.slug === this.tab);
        let nextTabIndex = currentTabIndex + 1;
        while (nextTabIndex < this.tabs.length && this.tabs[nextTabIndex].disabled) {
            nextTabIndex++;
        }

        // Do specific actions for each tab
        if (this.tab === 'category') {
            this.setLocationTab();
        }

        if (this.isEdit && nextTabIndex >= this.tabs.length) {
            void this.saveOffer();
            return;
        }

        this.selectTab(this.tabs[nextTabIndex].slug, true);
    }

    /**
     * Persists the current wizard state (PUT). Used by the normal "Save" flow and by resubmit (save then resubmit).
     */
    saveOfferUpdate$(): Observable<Offer> {
        if (!this.isEdit || !this.offerToEdit) {
            return throwError(() => new Error('saveOfferUpdate$: not in edit mode'));
        }
        const shippable = !!CategoryUtils.findProperty(this.data.categoryData.category, 'shippable');
        const body = buildUpdateOfferPayload(this.data, this.offerToEdit, shippable);
        return this.offerService.updateOffer(this.offerToEdit.id, body).pipe(
            tap((o) => {
                this.offerToEdit = o;
            }),
        );
    }

    async saveOffer() {
        if (!this.isEdit || !this.offerToEdit) {
            return;
        }
        if (this.submittingOffer) {
            return;
        }
        this.submittingOffer = true;
        await LoadingUtils.show();

        this.saveOfferUpdate$().subscribe({
            next: async (o) => {
                LoadingUtils.hide();
                this.submittingOffer = false;
                await this.toastService.success(_('Your offer has been updated'));
                await this.router.navigate(['/profile/offers']);
            },
            error: (err) => {
                LoadingUtils.hide();
                this.submittingOffer = false;
                this.toastService.error(this.formatBackendErrorForParent(err, _('Error updating offer')));
            },
        });
    }

    /** Same shaping as save errors; public for parent resubmit flow after PUT. */
    formatBackendErrorForParent(err: any, fallbackKey: string): string {
        const backendError = err?.error?.error;
        const details = err?.error?.details;
        let message = backendError || this.translateService.get(fallbackKey);
        const fieldErrors = this.flattenBackendDetails(details);
        if (fieldErrors) {
            message = `${message} — ${fieldErrors}`;
        }
        return message;
    }

    private flattenBackendDetails(details: unknown, prefix = ''): string {
        if (!details || typeof details !== 'object') {
            return '';
        }

        const parts: string[] = [];
        for (const [field, value] of Object.entries(details)) {
            const path = prefix ? `${prefix}.${field}` : field;
            if (Array.isArray(value)) {
                const msgs = value.filter((v): v is string => typeof v === 'string').join(', ');
                if (msgs) {
                    parts.push(`${path}: ${msgs}`);
                }
            } else if (value && typeof value === 'object') {
                const nested = this.flattenBackendDetails(value, path);
                if (nested) {
                    parts.push(nested);
                }
            } else if (typeof value === 'string') {
                parts.push(`${path}: ${value}`);
            }
        }
        return parts.join(' | ');
    }
}
